import React, { useContext, useRef } from 'react';
import { store } from 'components/Store';
import { useLocation } from 'react-router-dom';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import changeLabelOfContent from 'components/Dashboard/ParliamentaryRecord/ChangeLabelOfContent';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import {
  ItemControllerActions,
  ListItemContainer,
  ListItemHighlightSection,
  ListItemLink,
} from 'components/Dashboard/ListControl/ListItemLayout';
import useListItemController from 'components/Dashboard/ListControl/useListItemController';
import time from 'utils/time';
import preventLink from 'components/Dashboard/TagsList/prevenLink';
const CommitteePublicationItem = ({ listRow }) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const location = useLocation();

  const { search } = state;
  const { contentResults } = useContentResults();
  const { list, activeSearch } = search;
  const { CommitteePublications } = contentResults;
  const { currentId } = CommitteePublications ?? {};

  const { listContext = {}, feedOverrides = {}, display = {}, interactions = {}, external = {} } = listRow ?? {};
  const { index, item, scrollBarRef, array, zIndex, listView } = listContext;
  const { explanations, feedItemFlags: propsFeedItemFlags } = feedOverrides;
  const { notShowShare, showHighlights, showContentTypeBadge, tagsFixedPosition, onlyEmbargoedTag, tooltipText } =
    display;
  const { itemOnclick, thumbDownProps, itemActionsProps = {}, showPriorityBadge, setActiveUpdate } = interactions;
  const { embedShowTags, itemFromWidget } = external;
  const feedItemFlags = propsFeedItemFlags ?? CommitteePublications?.feedItemFlags ?? [];

  const { dashboardSection } = useMainSectionParameter();

  const itemClass = () => {
    switch (item.house) {
      case 'commons':
        return 'legislation-commons';
      case 'lords':
        return 'legislation-lords';
      default:
        return 'legislation-grey';
    }
  };

  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useListItemController({
    item,
    index,
    feedItemFlags: propsFeedItemFlags,
    itemFromWidget,
    listView,
    thumbDownProps,
    showPriorityBadge,
  });

  const { rememberPosition } = useRememberPosition();

  const highlightConditional =
    getUrlParam('search') ||
    getUrlParam('or') ||
    getUrlParam('topic-id') ||
    location.pathname.includes('/stakeholders') ||
    location.pathname.startsWith('/embed') ||
    location.pathname.startsWith('/search-results') ||
    showHighlights;

  return (
    <>
      <ListItemLink
        to={locationDescriptor(`/committee-publication/${item.id}/${normalize(item.title)}`)}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
        onClick={(e) => {
          if (rateUpdatesMode && setActiveUpdate) {
            preventLink(e);
            setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
            return;
          }
          rememberPosition('CommitteePublications', scrollBarRef, index);
          if (itemOnclick) {
            itemOnclick(e);
          }
        }}
        tooltipText={tooltipText}
      >
        <ListItemContainer
          className={`my-2 ${currentId === item.id ? 'active' : ''} ${
            unseenConditional ? 'unseen-item' : ''
          } adjustment-unseen-dot-parliamentary`}
          as='li'
        >
          <div className='content-item'>
            {unseenConditional && <span className='unseen-dot' />}
            <div className='py-3 px-4 parliamentary-item-padding'>
              <div className='d-flex align-items-baseline'>
                <div className='flex-grow-1 pr-1 pr-sm-0'>
                  <div className='d-flex align-items-baseline'>
                    {showContentTypeBadge && (
                      <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                        <ContentTypeBadge objectType={item.type} name={'Committee publication'} />
                      </div>
                    )}
                    <h4 className='mb-2 content-item-title mb-1'>{item.title}</h4>
                  </div>
                  <div className={`w-100`}>
                    <p
                      className={`heading-date mb-0 ${
                        highlightConditional || dashboardSection ? '' : 'information-date'
                      } ${dashboardSection ? 'item-size-text' : ''}`}
                    >
                      {(item.house || item.contentType) && (
                        <>
                          <span className={`${itemClass()}`}>{`${
                            item.house && item.house !== 'n/a'
                              ? item.house.charAt(0).toUpperCase()
                              : changeLabelOfContent(item.contentType)
                          }${item.house && item.house !== 'n/a' ? item.house.slice(1) : ''}`}</span>
                          <span className='dot'></span>
                        </>
                      )}
                      {item.committee ? (
                        <>
                          {item.committee}
                          <span className='dot'></span>
                        </>
                      ) : (
                        ''
                      )}
                      {item.publicationType ? (
                        <>
                          {item.publicationType}
                          <span className='dot'></span>
                        </>
                      ) : (
                        ''
                      )}
                      <span className='date'>{time(item.dateTime).formatLongDate()}</span>
                    </p>
                  </div>
                </div>
                {!notShowShare && (
                  <ItemControllerActions
                    rateUpdatesMode={rateUpdatesMode}
                    ratingFeedback={feedItemFlags?.[index]?.importanceRating}
                    priorityBadge={
                      priorityBadgeCondition ? <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} /> : null
                    }
                    actions={
                      <GeneralItemActions
                        item={item}
                        index={index}
                        thumbDownProps={thumbDownProps}
                        itemActionsProps={itemActionsProps}
                      />
                    }
                  />
                )}
              </div>

              <ListItemHighlightSection
                show={highlightConditional}
                bodyRef={titleBodyComponent}
                contributorName={item.contributorName}
                bodyMarkup={createMarkup(bodySnippet)}
                tags={
                  ((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
                    <TagsLists
                      feedItemFlags={feedItemFlags}
                      explanations={explanations ?? CommitteePublications?.explanations}
                      index={index}
                      title={'Parliamentary record'}
                      section={'CommitteePublications'}
                      tagsFixedPosition={tagsFixedPosition}
                      scrollBarRef={scrollBarRef}
                      onlyEmbargoedTag={onlyEmbargoedTag}
                    />
                  )
                }
              />
            </div>
          </div>
        </ListItemContainer>
      </ListItemLink>
    </>
  );
};

export default CommitteePublicationItem;
